import { Temporal } from "temporal-polyfill";
import * as Sentry from "@sentry/browser";
import { readSessionMarker, writeSessionMarker } from "./session-marker";

/**
 * Marker key for the last recovery reload. Must live in session storage rather
 * than module state, which a reload discards.
 */
const RELOAD_MARKER_KEY = "saile.staleBundleReloadedAt";

/**
 * Long enough that a reload has finished and failed again before we give up,
 * short enough that a genuinely new deploy later in the session still recovers.
 */
const RELOAD_COOLDOWN_MS = 60_000;

const nowEpochMs = () => Temporal.Now.instant().epochMilliseconds;

const readLastReloadAt = (): number | null => {
  const raw = readSessionMarker(RELOAD_MARKER_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const writeLastReloadAt = () =>
  writeSessionMarker(RELOAD_MARKER_KEY, String(nowEpochMs()));

/**
 * Recovers from a stale bundle after a deploy.
 *
 * A client that loaded `index.html` before a deploy holds references to hashed
 * chunks that no longer exist, so the next lazily-loaded route rejects with
 * "Importing a module script failed" and the navigation dead-ends. Reloading
 * fetches the current `index.html` and its live chunk names. See Sentry
 * SAILE-WEB-BG.
 *
 * Must be installed before the app renders any lazy route.
 */
export const installStaleBundleReload = () => {
  window.addEventListener("vite:preloadError", (event) => {
    // We are handling this by reloading, so keep it from also surfacing as an
    // unhandled rejection.
    event.preventDefault();

    // Offline is the other reason a chunk fetch fails, and a reload makes that
    // strictly worse by discarding the running app.
    if (!navigator.onLine) {
      return;
    }

    const lastReloadAt = readLastReloadAt();
    const isRetryingTooSoon =
      lastReloadAt !== null && nowEpochMs() - lastReloadAt < RELOAD_COOLDOWN_MS;

    if (isRetryingTooSoon) {
      // Still failing right after a reload means the chunk is genuinely gone,
      // not merely stale. Report it instead of reloading forever.
      Sentry.captureException(event.payload, {
        tags: { stale_bundle_reload: "gave_up" },
      });
      return;
    }

    writeLastReloadAt();
    window.location.reload();
  });
};
